<div class="media-viewer">
  <!-- Empty state -->
  <div *ngIf="!data?.items?.length" class="empty-state text-600">
    {{ data?.emptyMessage || 'Nothing to show yet.' }}
  </div>

  <!-- Document list (and any non-image file kind) -->
  <ul *ngIf="data?.kind === 'document' && data?.items?.length" class="doc-list">
    <li
      *ngFor="let item of data.items; let i = index"
      class="surface-card shadow-2 doc-row"
    >
      <div class="flex flex-row align-items-center justify-content-between gap-3 p-3">
        <div class="flex align-items-center gap-3 flex-grow-1 min-w-0">
          <p-avatar [icon]="iconClass(item)" shape="circle"></p-avatar>

          <div class="flex flex-column gap-1 min-w-0">
            <span class="text-900 font-medium doc-name" [title]="fileName(item)">
              {{ fileName(item) }}
            </span>
            <p class="m-0 text-600 text-sm" *ngIf="item._file?.size as size">
              {{ size | fileSize }}
            </p>
          </div>
        </div>

        <div class="flex align-items-center">
          <button
            pButton
            pRipple
            type="button"
            icon="pi pi-download"
            class="p-button-rounded mr-2 p-button-outlined"
            pTooltip="Download"
            tooltipPosition="top"
            [showDelay]="500"
            (click)="download(item)"
          ></button>
          <button
            pButton
            pRipple
            type="button"
            icon="pi pi-external-link"
            class="p-button-rounded"
            [pTooltip]="isViewable(item) ? 'Preview' : 'Preview not available'"
            tooltipPosition="top"
            [showDelay]="500"
            [disabled]="!isViewable(item)"
            (click)="openPreview(i)"
          ></button>
        </div>
      </div>
    </li>
  </ul>

  <!-- Image gallery -->
  <div *ngIf="data?.kind === 'image' && data?.items?.length" class="gallery">
    <div *ngIf="loadingImages" class="text-600 loading">Loading images…</div>
    <div class="gallery-grid">
      <figure
        *ngFor="let item of data.items; let i = index"
        class="surface-card shadow-2 gallery-item"
        [class.clickable]="!!imageUrls[item.id]"
        (click)="imageUrls[item.id] && openPreview(i)"
      >
        <img
          *ngIf="imageUrls[item.id]"
          [src]="imageUrls[item.id]"
          [alt]="fileName(item)"
          loading="lazy"
          (load)="onImageLoad(item, $event)"
        />
        <div *ngIf="!imageUrls[item.id]" class="img-placeholder">
          <i class="pi pi-image"></i>
        </div>
        <figcaption class="p-2">
          <span class="img-name text-900" [title]="fileName(item)">{{ fileName(item) }}</span>
          <span *ngIf="imageDims[item.id] as d" class="img-dims">{{ d.w }} × {{ d.h }} px</span>
          <button
            pButton
            type="button"
            icon="pi pi-download"
            class="p-button-text p-button-rounded p-button-sm img-download"
            pTooltip="Download"
            (click)="$event.stopPropagation(); download(item)"
          ></button>
        </figcaption>
      </figure>
    </div>
  </div>

  <div class="viewer-actions">
    <button pButton type="button" label="Close" class="p-button-text" (click)="close()"></button>
  </div>
</div>

<!-- ============================================================================
     Preview dialog
     Mirrors the document-library preview (document-item.component.html) so the
     two surfaces feel like one feature: large dialog, browser-native PDF viewer,
     filename + download in the header. Extends it with prev/next navigation,
     a counter, and keyboard control (←, →, Esc).
============================================================================ -->
<p-dialog
  *ngIf="previewItem as pi"
  [visible]="previewIndex !== null"
  (visibleChange)="$event ? null : closePreview()"
  [style]="{ width: '95vw', height: '95vh' }"
  appendTo="body"
  [draggable]="false"
  [modal]="true"
  [blockScroll]="true"
  styleClass="media-preview-dialog"
>
  <ng-template pTemplate="header">
    <div class="flex justify-content-between align-items-center w-full gap-3">
      <span class="font-semibold text-lg flex-grow-1 min-w-0 white-space-nowrap overflow-hidden text-overflow-ellipsis">
        {{ fileName(pi) }}
      </span>

      <span *ngIf="data.items.length > 1" class="text-600 text-sm white-space-nowrap">
        {{ (previewIndex || 0) + 1 }} / {{ data.items.length }}
      </span>

      <button
        *ngIf="data.items.length > 1"
        pButton
        pRipple
        type="button"
        icon="pi pi-chevron-left"
        class="p-button-rounded p-button-outlined"
        pTooltip="Previous (←)"
        tooltipPosition="bottom"
        [showDelay]="500"
        (click)="previewPrev()"
      ></button>
      <button
        *ngIf="data.items.length > 1"
        pButton
        pRipple
        type="button"
        icon="pi pi-chevron-right"
        class="p-button-rounded p-button-outlined"
        pTooltip="Next (→)"
        tooltipPosition="bottom"
        [showDelay]="500"
        (click)="previewNext()"
      ></button>

      <button
        pButton
        pRipple
        type="button"
        icon="pi pi-download"
        label="Download"
        class="p-button-rounded"
        (click)="download(pi)"
      ></button>
    </div>
  </ng-template>

  <div class="preview-body">
    <div *ngIf="previewLoading" class="text-600 p-4">Loading preview…</div>

    <!-- PDF: use the browser's built-in viewer via <iframe> (matches document-item) -->
    <iframe
      *ngIf="!previewLoading && previewUrl && isPdf(pi)"
      [frameBorder]="0"
      [src]="previewUrl | safe: 'resourceUrl'"
      width="100%"
      height="100%"
    ></iframe>

    <!-- Image: render directly, scaled to fit -->
    <div
      *ngIf="!previewLoading && previewUrl && isImage(pi)"
      class="image-preview"
    >
      <img [src]="previewUrl" [alt]="fileName(pi)" />
    </div>
  </div>
</p-dialog>
